import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface UserPersonalDetails {
    id: number
    firstName?: string
    lastName?: string
    middleName?: string
    nickName?: string
    title?: string
    gender?: string
    dateOfBirth?: number
    primaryEmail?: string
    concatName?: string
    countryOfResidence?: string
    countryOfResidenceId?: string
    homeAirport?: string
    homeAirportId?: string
    age?: number
    createdAt?: string
    updatedAt?: string
}

export interface UserTravellerDetails {
    id: number
    isRegistered: boolean
    personalDetails?: UserPersonalDetails
    contactInfo?: any
    profileData?: any
    ffnPreferences?: any[]
    preferences?: any
    docDetails?: any[]
    userStatus?: string
    createdAt?: string
    updatedAt?: string
}

export interface UserDetailsResponse {
    id: number
    username?: string | null
    screenName?: string | null
    mobile?: string | null
    countryCode?: string | null
    mobileVerified?: boolean
    email?: string | null
    emailVerified?: boolean
    internal?: number | null
    profilePercentCompleted?: number | null
    travellerDetails?: UserTravellerDetails[]
    companyDetails?: any[]
    depositAccounts?: any[] | null
    gstDetails?: any[]
    resources?: any[] | null
    savedCards?: any | null
    peopleNudge?: any | null
    registrationDate?: string
    lastUpdatedOn?: string
}

export const userDetailsApi = createApi({
    reducerPath: 'userDetailsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
        credentials: 'include',
        prepareHeaders: headers => {
            headers.set('accept', '*/*')
            const caller = typeof window !== 'undefined' ? window.location.origin : 'https://me.flyin.com'
            headers.set('caller', caller)
            headers.set('content-type', 'application/json')
            return headers
        }
    }),
    endpoints: builder => ({
        fetchUserDetails: builder.query<UserDetailsResponse, void>({
            query: () => ({
                url: '/accounts-api/people/v2/fetch/details',
                method: 'GET'
            }),
            transformResponse: (response: UserDetailsResponse) => {
                // Priority: mobile number > username/email > firstName
                const mobile = response.mobile
                const countryCode = response.countryCode
                const username = response.username || response.screenName
                const firstName = response.travellerDetails?.[0]?.personalDetails?.firstName
                const lastName = response.travellerDetails?.[0]?.personalDetails?.lastName

                // Format mobile with country code if available
                const formattedMobile = mobile && countryCode ? `${countryCode}${mobile}` : mobile

                // Determine display name based on priority
                const displayName = formattedMobile || username || firstName || 'User'
                const fullName = firstName && lastName ? `${firstName} ${lastName}` : displayName

                return {
                    ...response,
                    // Add computed fields for easier access
                    displayName,
                    fullName,
                    formattedMobile
                }
            }
        })
    })
})

export const { useFetchUserDetailsQuery, useLazyFetchUserDetailsQuery } = userDetailsApi